/**
 * Executive calendar change set — before/after launch deltas + reconfirm list.
 */

import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, GitCompareArrows, Users } from 'lucide-react';
import type { CalendarChangeSet } from '../../../lib/timePhasedPlanning.types';
import { PLACEMENT_STATUS_LABELS } from './timePhasedUiShared';
import { getItemCountState } from '../../collection/types';

const CARD =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] ';

const TEAM_LABEL: Record<string, string> = {
  marketing: 'Product Marketing',
  legal: 'Legal',
  design: 'Design',
  support: 'Customer Support',
  finance: 'Finance',
  engineering: 'Engineering',
};

function teamLabel(team: string): string {
  return TEAM_LABEL[team] ?? team;
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'moved':
      return 'Moved';
    case 'displaced':
      return 'Displaced';
    case 'launchability_changed':
      return 'Launchability';
    default:
      return 'Status';
  }
}

export function CalendarChangeSetPanel({
  changeSet,
  onOpenHypothesis,
}: {
  changeSet: CalendarChangeSet;
  onOpenHypothesis?: (hypothesisScenarioId: string) => void;
}) {
  const { summary, launchDeltas, reconfirm, overloadDeltas, moneyPeriodDeltas, fundingContributors } =
    changeSet;
  const launchState = getItemCountState(launchDeltas.length);
  const reconfirmState = getItemCountState(reconfirm.length);
  const leverLine =
    changeSet.lever === 'promote'
      ? `Promote “${changeSet.leverPriorityName}” to top`
      : `Delay “${changeSet.leverPriorityName}” by ${changeSet.delayWeeks ?? 4} weeks`;

  return (
    <div className={`${CARD} p-5 space-y-4 border-primary-200 dark:border-primary-800/40`}>
      <div className="flex items-start gap-2">
        <GitCompareArrows className="h-4 w-4 text-primary-600 dark:text-primary-400 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Calendar change set
          </h2>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{leverLine}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { label: 'Launches changed', value: summary.launchesChanged },
          { label: 'Must reconfirm', value: summary.reconfirmCount },
          { label: 'New overload weeks', value: summary.newOverloadBuckets },
          { label: 'Lost launchable', value: summary.newlyNotLaunchable },
          {
            label: 'Funding overage weeks',
            value: summary.newFundingOverageBuckets ?? 0,
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-[var(--surface)] dark:bg-[var(--surface)] px-3 py-2"
          >
            <div className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {s.value}
            </div>
            <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">{s.label}</div>
          </div>
        ))}
      </div>

      {launchState === 'zero' ? (
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          No placement changes — capacity may already be saturated.
        </p>
      ) : (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] mb-2">
            What moved
          </h3>
          <ul className="space-y-2">
            {(launchState === 'many' ? launchDeltas.slice(0, 12) : launchDeltas).map((d) => (
              <li
                key={d.priorityId}
                className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {d.priorityName}
                  </span>
                  <span className="text-[11px] uppercase tracking-wide text-[var(--mustard)] dark:text-[var(--mustard)]">
                    {kindLabel(d.kind)}
                  </span>
                </div>
                <div className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1">
                  {PLACEMENT_STATUS_LABELS[d.baseStatus]} →{' '}
                  {PLACEMENT_STATUS_LABELS[d.hypothesisStatus]}
                  {d.deltaStartWeeks != null && d.deltaStartWeeks !== 0 && (
                    <span className="ml-1">
                      ({d.deltaStartWeeks > 0 ? '+' : ''}
                      {d.deltaStartWeeks} wk)
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 font-mono">
                  {(d.baseFeasibleStart ?? '—') + ' → ' + (d.hypothesisFeasibleStart ?? '—')}
                </div>
                {d.baseLaunchable !== d.hypothesisLaunchable && (
                  <div
                    className={`text-xs mt-1 ${
                      d.hypothesisLaunchable
                        ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                        : 'text-[var(--mustard)] dark:text-[var(--mustard)]'
                    }`}
                  >
                    {d.baseLaunchable ? 'Launchable' : 'Not launchable'} →{' '}
                    {d.hypothesisLaunchable ? 'Launchable' : 'Not launchable'}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {launchState === 'many' && launchDeltas.length > 12 && (
            <p className="text-xs text-[var(--text-ter)] mt-1">
              Showing 12 of {launchDeltas.length} changed launches
            </p>
          )}
        </div>
      )}

      {reconfirmState !== 'zero' && (
        <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)]/50 dark:bg-[var(--mustard)]/10 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)]" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--mustard)] dark:text-[var(--mustard)]">
              Must reconfirm ({summary.reconfirmCount})
            </h3>
          </div>
          <ul className="space-y-1.5 text-sm">
            {(reconfirmState === 'many' ? reconfirm.slice(0, 10) : reconfirm).map((r, i) => (
              <li key={`${r.priorityId}-${r.team}-${i}`} className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                <span className="font-medium">{teamLabel(r.team)}</span>
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]"> on </span>
                {r.priorityName}
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{r.reason}</div>
              </li>
            ))}
          </ul>
          <Link
            to="/planning/engine/participation?from=launch-calendar"
            className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Open Team Participation
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {overloadDeltas.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-[var(--coral)]" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)]">
              New overload pressure
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {overloadDeltas.slice(0, 16).map((o) => (
              <span
                key={`${o.resourceType}-${o.bucketStart}`}
                className="text-xs px-2 py-1 bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]"
                title={`${o.baseUtilizationPct}% → ${o.hypothesisUtilizationPct}%`}
              >
                {teamLabel(o.resourceType)} · {o.bucketStart} · {o.hypothesisUtilizationPct}%
              </span>
            ))}
          </div>
        </div>
      )}

      {(moneyPeriodDeltas?.length ?? 0) > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] mb-2">
            Funding periods that moved ($)
          </h3>
          {summary.fundingOverageTotal != null && summary.fundingOverageTotal > 0 && (
            <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)] mb-2">
              Hypothesis funding overage: ${Math.round(summary.fundingOverageTotal).toLocaleString()}
            </p>
          )}
          <ul className="space-y-1 text-sm">
            {moneyPeriodDeltas!.slice(0, 8).map((m) => (
              <li
                key={m.bucketStart}
                className="flex flex-wrap gap-2 text-[var(--text-pri)] dark:text-[var(--text-pri)] font-mono text-xs"
              >
                <span>{m.bucketStart}</span>
                <span>
                  ${m.baseDemand.toLocaleString()} → ${m.hypothesisDemand.toLocaleString()}
                </span>
                <span className={m.delta > 0 ? 'text-[var(--mustard)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                  ({m.delta > 0 ? '+' : ''}
                  {m.delta.toLocaleString()})
                </span>
              </li>
            ))}
          </ul>
          {(fundingContributors?.length ?? 0) > 0 && (
            <p className="text-xs text-[var(--text-ter)] mt-2">
              Top contributors:{' '}
              {fundingContributors!
                .slice(0, 3)
                .map((c) => `${c.priorityName} ($${Math.round(c.forecastDemand).toLocaleString()})`)
                .join(' · ')}
            </p>
          )}
        </div>
      )}

      {changeSet.hypothesisScenarioId && (
        <div className="flex flex-wrap gap-3 text-sm pt-1">
          {onOpenHypothesis && (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium text-primary-600 dark:text-primary-400 hover:underline"
              onClick={() => onOpenHypothesis(changeSet.hypothesisScenarioId)}
            >
              Open hypothesis calendar
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          <Link
            to={`/planning/engine/compare?baseline=${encodeURIComponent(changeSet.baseScenarioId)}&hypothesis=${encodeURIComponent(changeSet.hypothesisScenarioId)}`}
            className="inline-flex items-center gap-1 font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Compare to baseline
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
